import React, { useState, useCallback } from 'react';
import { api, getErrorMessage } from '../services/api';
import { GridLevel, GridLevelEdit } from '../types';

interface GridLevelEditorProps {
  runnerId: number;
  buyLevels: GridLevel[];
  sellLevels: GridLevel[];
  isActive: boolean;
  quoteCurrency: string;
  onSaved: () => void;
}

interface EditingCell {
  levelId: number;
  gridSide: 'buy' | 'sell';
  field: 'entryPrice' | 'exitPrice' | 'quantity';
}

export function GridLevelEditor({ runnerId, buyLevels, sellLevels, isActive, quoteCurrency, onSaved }: GridLevelEditorProps) {
  const [pendingEdits, setPendingEdits] = useState<Map<string, GridLevelEdit>>(new Map());
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const editKey = (gridSide: string, levelId: number) => `${gridSide}-${levelId}`;

  const startEdit = (level: GridLevel, field: 'entryPrice' | 'exitPrice' | 'quantity') => {
    const currentValue = level[field];
    setEditingCell({ levelId: level.id, gridSide: level.gridSide as 'buy' | 'sell', field });
    setEditValue(currentValue.toString());
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const val = parseFloat(editValue);
    if (isNaN(val) || val <= 0) {
      cancelEdit();
      return;
    }

    const key = editKey(editingCell.gridSide, editingCell.levelId);
    const existing = pendingEdits.get(key) || {
      levelId: editingCell.levelId,
      gridSide: editingCell.gridSide,
    };

    if (editingCell.field === 'entryPrice') existing.newEntryPrice = val;
    else if (editingCell.field === 'exitPrice') existing.newExitPrice = val;
    else if (editingCell.field === 'quantity') existing.newQuantity = val;

    const newEdits = new Map(pendingEdits);
    newEdits.set(key, existing);
    setPendingEdits(newEdits);
    cancelEdit();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitEdit();
    else if (e.key === 'Escape') cancelEdit();
  };

  const getEditedValue = (level: GridLevel, field: 'entryPrice' | 'exitPrice' | 'quantity'): number => {
    const key = editKey(level.gridSide, level.id);
    const edit = pendingEdits.get(key);
    if (!edit) return level[field];
    if (field === 'entryPrice' && edit.newEntryPrice !== undefined) return edit.newEntryPrice;
    if (field === 'exitPrice' && edit.newExitPrice !== undefined) return edit.newExitPrice;
    if (field === 'quantity' && edit.newQuantity !== undefined) return edit.newQuantity;
    return level[field];
  };

  const isEdited = (level: GridLevel, field: 'entryPrice' | 'exitPrice' | 'quantity'): boolean => {
    const key = editKey(level.gridSide, level.id);
    const edit = pendingEdits.get(key);
    if (!edit) return false;
    if (field === 'entryPrice') return edit.newEntryPrice !== undefined;
    if (field === 'exitPrice') return edit.newExitPrice !== undefined;
    if (field === 'quantity') return edit.newQuantity !== undefined;
    return false;
  };

  const handleSave = useCallback(async () => {
    if (pendingEdits.size === 0) return;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const edits = Array.from(pendingEdits.values());
      await api.updateGridLevels(runnerId, edits);
      setPendingEdits(new Map());
      setSuccess(`${edits.length} level(s) updated${isActive ? ' - changes will apply on next tick' : ''}`);
      onSaved();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [pendingEdits, runnerId, isActive, onSaved]);

  const discardEdits = () => {
    setPendingEdits(new Map());
    setError('');
    setSuccess('');
  };

  const getPhaseColor = (phase: string) => {
    switch (phase) {
      case 'buying': return '#4caf50';
      case 'selling': return '#f44336';
      default: return '#8892b0';
    }
  };

  const renderCell = (level: GridLevel, field: 'entryPrice' | 'exitPrice' | 'quantity') => {
    const isCurrentlyEditing = editingCell &&
      editingCell.levelId === level.id &&
      editingCell.gridSide === level.gridSide &&
      editingCell.field === field;

    if (isCurrentlyEditing) {
      return (
        <input
          type="number"
          step="any"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          autoFocus
          style={styles.editInput}
        />
      );
    }

    const displayValue = getEditedValue(level, field);
    const edited = isEdited(level, field);

    return (
      <span
        onClick={() => startEdit(level, field)}
        style={{
          ...styles.editableCell,
          ...(edited ? styles.editedCell : {}),
          cursor: 'pointer',
        }}
        title="Click to edit"
      >
        {displayValue.toFixed(field === 'quantity' ? 4 : 5)}
      </span>
    );
  };

  const renderLevelRow = (level: GridLevel) => {
    const prefix = level.gridSide === 'buy' ? 'B' : 'S';
    const side = String(level.gridSide);
    const id = Number(level.id);
    return (
      <tr key={`${side}-${id}`} style={styles.row}>
        <td style={styles.td}>
          <span style={{ color: level.gridSide === 'buy' ? '#4caf50' : '#f44336' }}>
            {prefix}{level.id}
          </span>
        </td>
        <td style={styles.td}>{renderCell(level, 'entryPrice')}</td>
        <td style={styles.td}>{renderCell(level, 'exitPrice')}</td>
        <td style={styles.td}>{renderCell(level, 'quantity')}</td>
        <td style={styles.td}>
          <span style={{ ...styles.phaseBadge, backgroundColor: getPhaseColor(level.phase) }}>
            {level.phase}
          </span>
        </td>
        <td style={styles.td}>
          <span style={{ color: level.orderId ? '#4caf50' : '#8892b0', fontSize: '12px' }}>
            {level.orderId ? 'Active' : 'None'}
          </span>
        </td>
        <td style={styles.td}>{level.roundTrips}</td>
        <td style={{ ...styles.td, color: level.realizedPnl >= 0 ? '#4caf50' : '#f44336' }}>
          {level.realizedPnl.toFixed(4)} {quoteCurrency}
        </td>
      </tr>
    );
  };

  return (
    <div>
      {error && <div style={styles.errorMsg}>{error}</div>}
      {success && <div style={styles.successMsg}>{success}</div>}

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Level</th>
              <th style={styles.th}>Entry Price</th>
              <th style={styles.th}>Exit Price</th>
              <th style={styles.th}>Quantity</th>
              <th style={styles.th}>Phase</th>
              <th style={styles.th}>Order</th>
              <th style={styles.th}>RTs</th>
              <th style={styles.th}>P&L</th>
            </tr>
          </thead>
          <tbody>
            {buyLevels.length > 0 && (
              <tr>
                <td colSpan={8} style={styles.sideHeader}>
                  <span style={{ color: '#4caf50' }}>Buy Levels</span>
                </td>
              </tr>
            )}
            {buyLevels.map(renderLevelRow)}
            {sellLevels.length > 0 && (
              <tr>
                <td colSpan={8} style={styles.sideHeader}>
                  <span style={{ color: '#f44336' }}>Sell Levels</span>
                </td>
              </tr>
            )}
            {sellLevels.map(renderLevelRow)}
          </tbody>
        </table>
      </div>

      {pendingEdits.size > 0 && (
        <div style={styles.actions}>
          <span style={styles.editCount}>{pendingEdits.size} pending edit(s)</span>
          <button onClick={() => { void handleSave(); }} disabled={saving} style={styles.saveButton}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button onClick={discardEdits} style={styles.discardButton}>
            Discard
          </button>
        </div>
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    color: '#8892b0',
    fontSize: '11px',
    textTransform: 'uppercase',
    borderBottom: '1px solid #233554',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '8px 12px',
    color: '#ccd6f6',
    fontSize: '14px',
    borderBottom: '1px solid rgba(35, 53, 84, 0.5)',
  },
  row: {},
  sideHeader: {
    padding: '10px 12px',
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase' as const,
    borderBottom: '1px solid #233554',
    backgroundColor: 'rgba(35, 53, 84, 0.3)',
  },
  editableCell: {
    padding: '4px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    minWidth: '80px',
    transition: 'background-color 0.15s',
  },
  editedCell: {
    backgroundColor: 'rgba(255, 152, 0, 0.2)',
    border: '1px solid rgba(255, 152, 0, 0.4)',
  },
  editInput: {
    width: '100px',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #4caf50',
    backgroundColor: '#0d1117',
    color: '#ccd6f6',
    fontSize: '14px',
    outline: 'none',
  },
  phaseBadge: {
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    color: 'white',
    fontWeight: 'bold',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #233554',
  },
  editCount: {
    color: '#ff9800',
    fontSize: '14px',
  },
  saveButton: {
    padding: '8px 20px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#4caf50',
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  discardButton: {
    padding: '8px 20px',
    borderRadius: '6px',
    border: '1px solid #8892b0',
    backgroundColor: 'transparent',
    color: '#8892b0',
    fontSize: '14px',
    cursor: 'pointer',
  },
  errorMsg: {
    backgroundColor: 'rgba(233, 69, 96, 0.2)',
    border: '1px solid #e94560',
    color: '#e94560',
    padding: '8px 12px',
    borderRadius: '6px',
    marginBottom: '12px',
    fontSize: '14px',
  },
  successMsg: {
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    border: '1px solid #4caf50',
    color: '#4caf50',
    padding: '8px 12px',
    borderRadius: '6px',
    marginBottom: '12px',
    fontSize: '14px',
  },
};
